import React, { useState } from "react";
import { Visibility } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tooltip,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { FraudBlock } from "../../../../types/fraud.types";
import { Colors } from "../../../../constants/Colors";

interface Props {
  block: FraudBlock;
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.5, sm: 2 },
      py: 1,
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: Colors.commons.darkGray, minWidth: { sm: 140 } }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, wordBreak: "break-word" }}>{children}</Box>
  </Box>
);

const ChipList: React.FC<{ items: string[] }> = ({ items }) => {
  if (!items?.length) return <Typography variant="body2">—</Typography>;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {items.map((item, index) => (
        <Chip key={`${item}-${index}`} size="small" label={item} />
      ))}
    </Box>
  );
};

const ViewBlockDetails: React.FC<Props> = ({ block }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Ver mais" placement="top">
        <Visibility
          sx={{
            color: Colors.primary[100],
            cursor: "pointer",
            ":hover": { opacity: "70%" },
          }}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do bloqueio</DialogTitle>
        <DialogContent dividers>
          <DetailRow label="ID">
            <Typography variant="body2">{block.id}</Typography>
          </DetailRow>
          <DetailRow label="Último IP">
            <Typography variant="body2">{block.lastIp || "—"}</Typography>
          </DetailRow>
          <DetailRow label="Bloqueado">
            <Chip
              size="small"
              label={block.blocked ? "Sim" : "Não"}
              color={block.blocked ? "error" : "success"}
            />
          </DetailRow>
          <Divider />
          <DetailRow label="Transações">
            <Typography variant="body2">{block.txCount}</Typography>
          </DetailRow>
          <DetailRow label="Pix">
            <Typography variant="body2">{block.pixCount}</Typography>
          </DetailRow>
          <DetailRow label="Falhas">
            <Typography variant="body2">{block.failureCount}</Typography>
          </DetailRow>
          <DetailRow label="Score de falha">
            <Typography variant="body2">{block.failureScore}</Typography>
          </DetailRow>
          <Divider />
          <DetailRow label="Cartões (tokens)">
            <ChipList items={block.cardTokens} />
          </DetailRow>
          <DetailRow label="Dispositivos">
            <ChipList items={block.deviceIds} />
          </DetailRow>
          <DetailRow label="Usuários">
            <ChipList items={block.userIds} />
          </DetailRow>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewBlockDetails;
